import { useCallback } from "react";

import { dispatchHomeLayoutPatch } from "@/features/dashboard/home/kaiHomeLayoutBridge";
import type { HomeLayoutPatch } from "@/types/home";

/** Narrow an arbitrary SSE payload into a well-formed `HomeLayoutPatch`. */
function normalizePatch(raw: unknown): HomeLayoutPatch | null {
  if (!raw || typeof raw !== "object") return null;
  const patch = raw as Record<string, unknown>;
  switch (patch.op) {
    case "add_widget":
      return patch.widget && typeof patch.widget === "object"
        ? ({ op: "add_widget", widget: patch.widget } as HomeLayoutPatch)
        : null;
    case "update_widget":
      return typeof patch.widget_key === "string"
        ? ({
            op: "update_widget",
            widget_key: patch.widget_key,
            fields: (patch.fields as Record<string, unknown>) ?? {},
          } as HomeLayoutPatch)
        : null;
    case "remove_widget":
      return typeof patch.widget_key === "string"
        ? ({ op: "remove_widget", widget_key: patch.widget_key } as HomeLayoutPatch)
        : null;
    case "replace_all":
      return Array.isArray(patch.widgets)
        ? ({ op: "replace_all", widgets: patch.widgets } as HomeLayoutPatch)
        : null;
    default:
      return null;
  }
}

/**
 * Applies a home-layout patch requested by Kai (via the
 * `apply_home_layout_patch` SSE event) as a live preview. The Home page's
 * `useHomeEditor` picks up the change through the `kai-home-layout-patch`
 * window event. Malformed payloads are ignored, and the preview only lands
 * while the Home page is mounted.
 */
export function useApplyKaiHomeLayout() {
  return useCallback((raw: unknown) => {
    const patch = normalizePatch(raw);
    if (patch) dispatchHomeLayoutPatch(patch);
  }, []);
}
